import React, {FC} from "react";
import {
  CheckboxesIncludedChoices,
  History,
  OptionResponseItem,
} from "ts/api/task";
import {IGetOneByIdTaskOption} from "@evennt-tech/api-v2-external";
import useStyles from "./styles";

/** Minimal shape consumed by `getSelectedCheckboxesWithNestedOptions`. */
export type CheckboxesTaskLike = {
  optionsResponse?: OptionResponseItem[];
  checkboxesIncludedChoices?: CheckboxesIncludedChoices;
  multipleChoiceIncludedChoices?: {[k: string]: string};
};

/**
 * Two modes: `options` set consumes the deep-task
 * `IGetOneByIdTaskOption[]`; otherwise `task` provides
 * `optionsResponse[]` + `checkboxesIncludedChoices`.
 */
type Props = {
  checkboxes: string[];
  historyItem?: History;
  task?: CheckboxesTaskLike;
  options?: IGetOneByIdTaskOption[];
};

export const getSelectedCheckboxesWithNestedOptions = (
  checkboxes: string[],
  task: CheckboxesTaskLike
): OptionResponseItem[] => {
  return checkboxes
    ?.map((checkbox) => {
      const optionResponseItem = task?.optionsResponse?.find(
        (item) => item.key === checkbox
      );
      if (!optionResponseItem) {
        return null;
      }
      if (!optionResponseItem?.nestedOptions?.length) {
        return optionResponseItem;
      }
      const nestedOptions = optionResponseItem?.nestedOptions.filter(
        (option) =>
          task?.checkboxesIncludedChoices &&
          task?.checkboxesIncludedChoices[checkbox]?.includes(option.key)
      );

      return {...optionResponseItem, nestedOptions};
    })
    .filter(Boolean) as OptionResponseItem[];
};

const renderListWithNestedOptions = (responses: OptionResponseItem[]) => {
  const classes = useStyles();

  return (
    <ul className={classes.list}>
      {responses?.map((response) => (
        <li key={response.key} className={classes.item}>
          {response.name}
          {response?.nestedOptions && (
            <ul className={classes.list}>
              {response?.nestedOptions?.map((option) => (
                <li key={response.key} className={classes.item}>
                  {option.name}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};
/**
 * Filters the option tree against `selectedIds` and groups nested
 * options under their parent, producing the `OptionResponseItem[]` shape
 * the renderer consumes downstream.
 *
 * Defensive: an orphan nested pick (child selected without its parent)
 * is surfaced as a top-level row so the exhibitor's intent isn't
 * silently dropped on a malformed payload.
 */
const buildSelectedFromOptions = (
  selectedIds: string[],
  options: IGetOneByIdTaskOption[]
): OptionResponseItem[] => {
  const selected = new Set(selectedIds);
  const result = options
    .filter((o) => !o.parentOptionId && selected.has(o.id))
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((parent) => {
      const nestedOptions = options
        .filter((c) => c.parentOptionId === parent.id && selected.has(c.id))
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((c) => ({key: c.id, name: c.name}));
      return {
        key: parent.id,
        name: parent.name,
        ...(nestedOptions.length ? {nestedOptions} : {}),
      } as OptionResponseItem;
    });
  // Orphan rescue: nested children whose parent isn't selected get
  // rendered alongside top-level entries.
  const renderedIds = new Set(result.flatMap((r) => [r.key]));
  const orphans = options
    .filter(
      (o) =>
        o.parentOptionId &&
        selected.has(o.id) &&
        !renderedIds.has(o.parentOptionId)
    )
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((o) => ({key: o.id, name: o.name} as OptionResponseItem));
  return [...result, ...orphans];
};

const CheckboxesResponse: FC<Props> = ({
  task,
  historyItem,
  checkboxes,
  options,
}) => {
  if (options) {
    return renderListWithNestedOptions(
      buildSelectedFromOptions(checkboxes, options)
    );
  }
  const historyItemExistedWithNestedOptions =
    historyItem &&
    historyItem?.optionsResponse &&
    historyItem?.checkboxesIncludedChoices;
  if (historyItemExistedWithNestedOptions) {
    const historyResponses = getSelectedCheckboxesWithNestedOptions(
      checkboxes,
      {...task, ...historyItem}
    );
    return renderListWithNestedOptions(historyResponses);
  }
  const responses = getSelectedCheckboxesWithNestedOptions(
    checkboxes,
    task ?? {}
  );

  return renderListWithNestedOptions(responses);
};

export default CheckboxesResponse;
